import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";
import { api, getNetworkConfig } from "@/lib/api";
import { useWallet } from "@/contexts/WalletContext";
import { addLiquidity, removeLiquidity, liquidityErrorMessage } from "@/lib/liquidity";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TestTokenNote } from "@/components/common/TestTokenNote";
import { TxLink } from "@/components/exchange/TradeParts";
import { PoolStats, PositionCard, AddPanel, RemovePanel } from "@/components/liquidity/LiquidityParts";
import { PoolActivity } from "@/components/liquidity/PoolActivity";

const getPool = (address) => api.get("/liquidity", { params: address ? { address } : {} }).then((r) => r.data);

export default function LiquidityPage() {
  const { address, getProvider, refreshBalances } = useWallet();
  const queryClient = useQueryClient();
  const { data: config } = useQuery({ queryKey: ["networkConfig"], queryFn: getNetworkConfig });
  const { data: pool, refetch } = useQuery({ queryKey: ["lpPool", address], queryFn: () => getPool(address), refetchInterval: 15000 });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [lastTx, setLastTx] = useState(null);

  const run = async (fn, success) => {
    setBusy(true);
    try {
      setLastTx(await fn(setStep));
      toast.success(success);
      refetch();
      queryClient.invalidateQueries({ queryKey: ["lpActivity"] });
      setTimeout(() => refreshBalances(), 1500);
    } catch (err) {
      toast.error(liquidityErrorMessage(err));
    } finally {
      setBusy(false);
      setStep("");
    }
  };
  const onAdd = (fmiWei, reset) =>
    run((onStep) => addLiquidity({ eip1193: getProvider(), pool, usdtAddress: config.usdt.address, fmiWei, onStep }).then((h) => { reset(); return h; }),
      "Liquidity added to the FMI/USDT pool");
  const onRemove = (sharesRaw) =>
    run((onStep) => removeLiquidity({ eip1193: getProvider(), pool, sharesRaw, onStep }), "Liquidity withdrawn to your wallet");

  return (
    <div data-testid="liquidity-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP}>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">FMI <span className="text-[#D4AF37]">liquidity pool</span></h1>
        <p className="text-zinc-400 mb-3">Deposit FMI and USDT into the community pool, earn {pool?.feePercent ?? 0.2}% of every swap routed through it, and withdraw anytime.</p>
        <TestTokenNote testId="lp-token-note" />
      </motion.div>
      <div className="mt-8 space-y-6">
        <PoolStats pool={pool} />
        {address && <PositionCard position={pool?.position} />}
        <Card className="border-[#2A2411] bg-[#0A0A0A]/80">
          <CardContent className="p-5">
            <Tabs defaultValue="add">
              <TabsList className="grid grid-cols-2 bg-[#050505] mb-5">
                <TabsTrigger data-testid="lp-tab-add" value="add">Add</TabsTrigger>
                <TabsTrigger data-testid="lp-tab-remove" value="remove">Withdraw</TabsTrigger>
              </TabsList>
              <TabsContent value="add"><AddPanel pool={pool} address={address} busy={busy} step={step} onAdd={onAdd} /></TabsContent>
              <TabsContent value="remove"><RemovePanel pool={pool} position={pool?.position} busy={busy} step={step} onRemove={onRemove} /></TabsContent>
            </Tabs>
            <TxLink hash={lastTx} testId="lp-tx-link" label="View transaction" receipt={false} />
          </CardContent>
        </Card>
        <PoolActivity />
        <p className="text-xs text-zinc-600">
          Pool contract <span data-testid="lp-pool-address" className="font-mono text-zinc-400 break-all">{pool?.address}</span>. Deposits follow the pool's current FMI/USDT ratio; withdrawals return your share of both tokens, including earned fees. Your share can be worth more or less than what you put in as the price moves.
        </p>
      </div>
    </div>
  );
}
